/**
 * 坦克大战 3D - 音效系统 (Web Audio API 程序化生成)
 * 无需外部音频文件，所有音效实时合成。
 */
export class AudioSystem {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  private musicOscillators: OscillatorNode[] = [];
  private musicPlaying = false;
  private initialized = false;

  /** 首次用户交互后调用以解锁 AudioContext */
  init(): void {
    if (this.initialized) return;
    try {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.35;
      this.masterGain.connect(this.ctx.destination);

      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.25;
      this.musicGain.connect(this.masterGain);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = 0.7;
      this.sfxGain.connect(this.masterGain);

      this.initialized = true;
    } catch {
      // 静默处理不支持的情况
    }
  }

  private ensureCtx(): AudioContext | null {
    if (!this.initialized) this.init();
    if (!this.ctx || this.ctx.state === 'closed') return null;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  // ==================== 背景音乐 ====================

  startMusic(): void {
    if (this.musicPlaying) return;
    const ctx = this.ensureCtx();
    if (!ctx || !this.musicGain) return;
    this.musicPlaying = true;

    const now = ctx.currentTime;
    // 低频嗡声 (氛围基底)
    this.createDrones(ctx, now);
    // 节奏低音
    this.scheduleBass(ctx, now);
  }

  private createDrones(ctx: AudioContext, now: number): void {
    // 两个低频正弦波 + LFO 颤音
    const freqs = [55, 82.4]; // A1, E2
    for (const freq of freqs) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.15;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 2;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      lfo.start(now);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.08, now + 0.5);
      osc.connect(gain);
      gain.connect(this.musicGain!);

      osc.start(now);
      this.musicOscillators.push(osc, lfo);
    }
  }

  private scheduleBass(ctx: AudioContext, _startTime: number): void {
    const barDuration = 2.0; // 每小节2秒
    const pattern = [1, 0, 0, 0.5, 0, 0.7, 0, 0.3]; // 节奏模式
    const baseFreq = 110; // A2

    const scheduleLoop = () => {
      if (!this.musicPlaying || !this.musicGain) return;
      const now = ctx.currentTime;

      for (let i = 0; i < pattern.length; i++) {
        const vel = pattern[i];
        if (vel === 0) continue;

        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = baseFreq * (1 + i * 0.05);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.1 * vel, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + barDuration * 0.3);

        osc.connect(gain);
        gain.connect(this.musicGain!);
        osc.start(now);
        osc.stop(now + barDuration * 0.4);
      }

      this.musicTimer = setTimeout(scheduleLoop, barDuration * 1000);
    };

    scheduleLoop();
  }

  private musicTimer: ReturnType<typeof setTimeout> | null = null;

  stopMusic(): void {
    this.musicPlaying = false;
    if (this.musicTimer) { clearTimeout(this.musicTimer); this.musicTimer = null; }
    for (const osc of this.musicOscillators) {
      try { osc.stop(); } catch { /* already stopped */ }
    }
    this.musicOscillators.length = 0;
  }

  // ==================== 音效 ====================

  /** 玩家开火 - 尖锐爆破音 */
  playPlayerFire(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    // 噪声爆破
    this.playNoiseBurst(ctx, now, 0.12, 0.35, '#f5e642', this.sfxGain);
    // 高频短音
    this.playTone(ctx, now, 800, 1200, 0.06, 'square', 0.2, this.sfxGain);
  }

  /** 敌人开火 - 低沉爆破音 */
  playEnemyFire(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    this.playNoiseBurst(ctx, now, 0.1, 0.25, '#e74c3c', this.sfxGain);
    this.playTone(ctx, now, 400, 600, 0.05, 'square', 0.15, this.sfxGain);
  }

  /** 爆炸 - 强烈噪音+低频 */
  playExplosion(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    // 长噪音爆破
    this.playNoiseBurst(ctx, now, 0.35, 0.7, '#ff6b35', this.sfxGain);
    // 低频轰隆
    this.playTone(ctx, now, 60, 30, 0.4, 'sawtooth', 0.5, this.sfxGain);
    // 二次冲击
    this.playTone(ctx, now + 0.05, 100, 40, 0.25, 'sine', 0.3, this.sfxGain);
  }

  /** 玩家受击 */
  playPlayerHit(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    this.playNoiseBurst(ctx, now, 0.08, 0.3, '#ff4444', this.sfxGain);
    this.playTone(ctx, now, 200, 80, 0.15, 'triangle', 0.3, this.sfxGain);
  }

  /** 拾取血包 */
  playPickup(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    // 上升音阶
    this.playTone(ctx, now, 600, 900, 0.08, 'sine', 0.2, this.sfxGain);
    this.playTone(ctx, now + 0.06, 900, 1200, 0.08, 'sine', 0.2, this.sfxGain);
    this.playTone(ctx, now + 0.12, 1200, 1600, 0.1, 'sine', 0.25, this.sfxGain);
  }

  /** 波次完成 */
  playWaveComplete(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      this.playTone(ctx, now + i * 0.12, freq, freq * 1.02, 0.2, 'triangle', 0.22, this.sfxGain!);
    });
  }

  /** 游戏结束 */
  playGameOver(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    const notes = [440, 370, 330, 220]; // 下行
    notes.forEach((freq, i) => {
      this.playTone(ctx, now + i * 0.2, freq, freq * 0.5, 0.3, 'sawtooth', 0.18, this.sfxGain!);
    });
  }

  // ==================== 底层音色生成 ====================

  private playTone(
    ctx: AudioContext,
    startTime: number,
    freqStart: number,
    freqEnd: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    destination: AudioNode,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freqStart, startTime);
    osc.frequency.linearRampToValueAtTime(freqEnd, startTime + duration);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(volume, startTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    osc.connect(gain);
    gain.connect(destination);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.01);
  }

  private playNoiseBurst(
    ctx: AudioContext,
    startTime: number,
    duration: number,
    volume: number,
    _color: string,
    destination: AudioNode,
  ): void {
    // 创建白噪声缓冲区
    const sampleRate = ctx.sampleRate;
    const length = Math.floor(sampleRate * duration);
    const buffer = ctx.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < length; i++) {
      const t = i / length;
      // 带通滤波效果：用正弦调制振幅模拟低频噪声
      data[i] = (Math.random() * 2 - 1) * (1 - t) * (0.5 + 0.5 * Math.sin(t * 40));
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    // 低通滤波
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, startTime);
    filter.frequency.exponentialRampToValueAtTime(100, startTime + duration);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    source.start(startTime);
    source.stop(startTime + duration);
  }

  // ==================== 清理 ====================

  dispose(): void {
    this.stopMusic();
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close().catch(() => {});
    }
    this.initialized = false;
  }
}
